import { Component, OnInit } from '@angular/core';
import { Platform, ToastController } from '@ionic/angular';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { PreferenzeApp } from './common/models/Preferenze';
import { AppUser } from './common/models/Utente';
import { LanguageService } from './common/sharedServices/language/language.service';
import { SsoService } from './common/sharedServices/sso/sso.service';
import { AuthenticationService } from './common/sharedServices/authentication/authentication.service';
import { PreferencesService } from './common/sharedServices/preferences/preferences.service';
import { UserService } from './common/sharedServices/user/user.service';
import { environment } from './../environments/environment';
import { Device } from '@capacitor/device';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Observable } from 'rxjs';
import { register } from 'swiper/element/bundle';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit {
  /**
   * Flag che determina la presenza del menu di navigazione
   * Di default l'app si apre sulla pagina di login e poi da li
   * magari fa l'auto login
   */
  isOnLoginPage = true;
  preferenze: PreferenzeApp;
  user$: Observable<AppUser>;
  /**
   * Numero di versione di quest'applicazione
   * nel formato semver (X.Y.Z) letto da file
   * environment.ts
   */
  APP_VERSION: string;
  /**
   * Numero di versione definito da Integra che potrebbe
   * essere compatibile con quest'app e dovrà 
   * essere <= versione di questo software
   */
  private COMPATIBILITY_VERSIONS: Array<string>;

  constructor(
    private platform: Platform,
    private translateService: LanguageService,
    private toastController: ToastController,
    private router: Router,
    private SSO: SsoService,
    private authenticationService: AuthenticationService,
    private preferenzeService: PreferencesService,
    private userService: UserService
  ) {
    this.initializeApp();
    register(); //funzione fondamentale per lo swiper
  }

  private async initializeApp() {
    this.APP_VERSION = environment.APP_VERSION;
    this.COMPATIBILITY_VERSIONS = environment.COMPATIBILITY;
    console.log(`Applicazione avviata da versione ${environment.APP_VERSION} del ${environment.APP_BUILD_DATE}`);

    await this.platform.ready();
    const device = await Device.getInfo();
    if (device.platform === 'ios' || device.platform === 'android') {
      StatusBar.setStyle({
        style: Style.Dark
      });
      SplashScreen.hide();
    }

    // Inizializzo i vari service
    this.translateService.initLanguageService();
    this.initializeObservers();
    this.checkAbilitazioneMenu();
  }

  ngOnInit() {

  }

  private async initializeObservers() {
    this.user$ = (await this.userService.getDatiUtenteObservable());
    (await this.preferenzeService.getPreferenzeUtenteObservable()).subscribe(pref => {
      this.preferenze = pref;
      document.body.classList.toggle('dark', pref.MODALITA_SCURA);
      /**
       * Commentato metodo sottostante "checkCompatibility" dato che, avendo tolto il middleware e
       * avendo tutti i ws rest, non capiterà mai il caso in cui l'app non sia compatibile con la versione del middleware
       * installato dal cliente (dato che appunto non esiste più)
       */
      //this.checkCompatibility(this.preferenze.VERSIONE_INTEGRA);
    });
  }

  private checkCompatibility(integraServerVersion: string) {
    if (integraServerVersion === '--') {
      console.warn('Installazione in sviluppo ?');
    } else if (integraServerVersion) {
      if (!this.isVersionCompatible(integraServerVersion.toUpperCase())) {
        console.error('Versione integra NON compatibile ', integraServerVersion);
        this.toastController.create({
          message: `Versione Server non compatibile (${integraServerVersion}); verifica presenza di aggiornamenti`,
          color: 'danger',
          position: 'bottom',
          header: '! Attenzione',
          buttons: [{
            role: 'cancel',
            icon: 'checkmark'
          }]
        }).then(toast => toast.present());
      }
    }
  }

  /**
   * Gestione di compatibilità basata sulla versione (x.y.z).
   * Il controllo viene effettuato su major (x) e minor (y) version, poichè un loro incremento è dovuto 
   * da modifiche strutturali dell'app o dall'aggiunta di funzionalità per cui è necessario aggiornare i 
   * servizi rest, e quindi non può essere garantita la retrocompatibilità.
   * @param version 
   * @returns 
   */
  private isVersionCompatible(version: string) {
    const [majorVersion, minorVersion] = version.split('.');
    return this.COMPATIBILITY_VERSIONS.some(compatibleVersion => {
      const [compatibleMajor, compatibleMinor] = compatibleVersion.split('.');
      return majorVersion === compatibleMajor && minorVersion === compatibleMinor;
    });
  }

  private checkAbilitazioneMenu() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.url.endsWith('login') || (event.url === '/')) {
          this.isOnLoginPage = true;
        } else {
          this.isOnLoginPage = false;
        }
      });
  }

  async logout() {
    await this.SSO.setLogoutSSOStatus();
    await this.authenticationService.logout();
    this.router.navigateByUrl('/login');
  }
}